import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { notify } from '@mrlannigan/reapop';
import { push } from 'connected-react-router';
import { isLoggedIn } from 'services/auth/selectors';
import { fetchCurrentUser } from 'services/users/duck';
import { getDisplayName } from 'helpers/component';
import LoadingDiv from 'components/atoms/LoadingDiv';
import { DASHBOARD_ACCESS } from 'constants/capabilities';

export default function ProtectedRouteEnhancer({
    store: storeKey = 'store',
    redirectTo = '/login', // redirect to login page if not logged in
    requiredCapabilities = [],
    silent = false,
    redirectToOnNoAccess = null,
    NoUserLoadedComponent = LoadingDiv, // what to render when no user is loaded yet
} = {}) {
    return function ProtectedRouteHOC(WrappedComponent) {
        class ProtectedRoute extends React.Component {
            constructor(props, context) {
                super(props);
                this.store = context[storeKey];

                this.state = {
                    isLoggedIn: false,
                    hasAccess: false,
                };
            }

            componentDidMount() {
                const state = this.store.getState();
                let fromUrl = state.router.location.pathname;
                if (!isLoggedIn(state)) {
                    if (fromUrl === '/') {
                        this.store.dispatch(push(redirectTo));
                    } else {
                        if (state.router.location.search) {
                            fromUrl += `${state.router.location.search}`;
                        }
                        this.store.dispatch(push(`${redirectTo}?from=${encodeURIComponent(fromUrl)}`));
                    }
                    this.setState({ isLoggedIn: false });
                } else {
                    this.setState({ isLoggedIn: true });
                    if (!this.props.currentUser) {
                        this.props.fetchCurrentUser({ disableNavigation: true });
                    } else {
                        this.hasRequiredCapabilities(this.props);
                    }
                }
            }

            hasRequiredCapabilities(props) {
                if (props.currentUser) {
                    const state = this.store.getState();

                    const check = requiredCapabilities.every(e =>
                        props.currentUser.scope.indexOf(e) > -1);

                    if (!check) {
                        if (!silent) {
                            props.notify({
                                title: 'Unauthorized Access',
                                message: 'You do not have permissions to view that page.',
                                status: 'error',
                                dismissible: true,
                                dismissAfter: 3000
                            });
                        }

                        if (state.router.location.pathname !== props.currentUser.landingPage) {
                            this.store.dispatch(push(redirectToOnNoAccess || props.currentUser.landingPage));
                        }
                    }

                    if (this.state.hasAccess !== check) {
                        this.setState({ hasAccess: check });
                    }

                    return check;
                }
                return false;
            }

            componentWillReceiveProps(nextProps) {
                this.hasRequiredCapabilities(nextProps);
            }

            render() {
                // Wraps the input component in a container, without mutating it. Good!
                const {
                    isLoggedIn: isUserLoggedIn,
                    hasAccess
                } = this.state;

                const {
                    currentUser
                } = this.props;

                // has access
                if (isUserLoggedIn && hasAccess) {
                    return <WrappedComponent {...this.props} />;
                }

                // doesn't have access
                if (currentUser && !hasAccess) {
                    return null;
                }

                // waiting to understand access
                if (!currentUser && NoUserLoadedComponent !== false) {
                    return <NoUserLoadedComponent {...this.props} />;
                }

                return null;
            }
        }

        ProtectedRoute.contextTypes = {
            [storeKey]: PropTypes.object
        };

        ProtectedRoute.displayName = `ProtectedRoute(${getDisplayName(WrappedComponent)})`;

        const mapStateToProps = state => ({
            currentUser: state.users.currentUser
        });

        return connect(mapStateToProps, { fetchCurrentUser, notify })(ProtectedRoute);
    };
}

export const DashboardsProtections = {
    requiredCapabilities: [DASHBOARD_ACCESS],
    silent: false,
    redirectToOnNoAccess: '/dashboard',
};



// WEBPACK FOOTER //
// ./src/hoc/ProtectedRoute.js